import { computed, onMounted, reactive, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useI18n } from "vue-i18n";
import Swal from "sweetalert2";

import transferService, { type Transfer } from "@/services/transfer";
import warehouseService, { type Warehouse } from "@/services/warehouse";
import settingService from "@/services/setting";
import productService, { type Product, type StockEntry } from "@/services/product";
import { useCurrentUser } from "@/composables/useCurrentUser";

type TransferProduct = Product & {
  qty: number;
  product_id: number;
  product_name: string;
  product_code: string;
  unit_cost: number;
  cost: number;
  tax: number;
  subtotal: number;
  available_stock: number;
};

type TransferFormState = {
  date: string;
  from_warehouse_id: number | null;
  to_warehouse_id: number | null;
  note: string;
  status: string;
  shipping: number;
  order_tax: number;
  discount: number;
  ref: string;
};

type TransferSeedOptions = {
  mode: "create" | "edit";
  transferId?: number;
};

const normalizeSearch = (value: string) => value.replace(/\s+/g, " ").trim();
const toNumber = (value?: number | string | null) => Number(value ?? 0);
const buildFallbackTransferRef = () => `TR-${Date.now()}`;

const createInitialForm = (): TransferFormState => ({
  date: new Date().toISOString().split("T")[0],
  from_warehouse_id: null,
  to_warehouse_id: null,
  note: "",
  status: "completed",
  shipping: 0,
  order_tax: 0,
  discount: 0,
  ref: "",
});

function buildTransferComposable({ mode, transferId }: TransferSeedOptions) {
  const router = useRouter();
  const { t } = useI18n();
  const { fetchCurrentUser } = useCurrentUser();

  const breadcrumbItems =
    mode === "edit"
      ? [
          { label: "transfer_list", to: "/transfer-list" },
          { label: "edit_transfer" },
        ]
      : [
          { label: "transfer_list", to: "/transfer-list" },
          { label: "create_transfer" },
        ];

  const warehouses = ref<Warehouse[]>([]);
  const searchResults = ref<TransferProduct[]>([]);
  const showResults = ref(false);
  const items = ref<TransferProduct[]>([]);
  const isLoadingData = ref(false);
  const fromWarehouseProfileId = ref<number | null>(null);
  const originalTransfer = ref<Transfer | null>(null);
  const originalItemsByProductId = ref<Record<number, number>>({});
  const originalFromWarehouseId = ref<number | null>(null);

  const form = reactive<TransferFormState>(createInitialForm());
  const fromWarehouseLockedToProfile = computed(() => fromWarehouseProfileId.value !== null);

  const getWarehouseStock = (stocks?: StockEntry[], warehouseId?: number | null) => {
    if (!stocks?.length || !warehouseId) return 0;

    return stocks
      .filter((stock) => Number(stock.warehouse_id) === Number(warehouseId))
      .reduce((total, stock) => total + toNumber(stock.qty ?? stock.quantity), 0);
  };

  const getEditableStock = (productId: number, stocks?: StockEntry[], warehouseId?: number | null) => {
    const liveStock = getWarehouseStock(stocks, warehouseId);

    if (
      mode === "edit" &&
      warehouseId &&
      originalFromWarehouseId.value &&
      Number(warehouseId) === Number(originalFromWarehouseId.value)
    ) {
      return liveStock + toNumber(originalItemsByProductId.value[productId]);
    }

    return liveStock;
  };

  const toTransferProduct = (product: Product): TransferProduct => {
    const unitCost = toNumber(product.cost);
    const availableStock = getEditableStock(Number(product.id), product.stocks, form.from_warehouse_id);

    return {
      ...product,
      product_id: Number(product.id),
      product_name: product.name,
      product_code: product.code,
      qty: 1,
      unit_cost: unitCost,
      cost: unitCost,
      tax: 0,
      subtotal: unitCost,
      available_stock: availableStock,
    };
  };

  const syncDestinationWarehouse = () => {
    if (Number(form.from_warehouse_id) === Number(form.to_warehouse_id)) {
      const fallbackDestination = warehouses.value.find(
        (warehouse) => Number(warehouse.id) !== Number(form.from_warehouse_id)
      );
      form.to_warehouse_id = Number(fallbackDestination?.id ?? null);
    }
  };

  const fetchBaseData = async () => {
    const [warehouseRes, settingsRes, currentUser] = await Promise.allSettled([
      warehouseService.getWarehouses(),
      settingService.getSettings(),
      fetchCurrentUser(true),
    ]);

    if (warehouseRes.status === "fulfilled") {
      warehouses.value = warehouseRes.value;
    }

    const currentUserWarehouseId =
      currentUser.status === "fulfilled" && currentUser.value?.warehouse_id
        ? Number(currentUser.value.warehouse_id)
        : null;

    fromWarehouseProfileId.value = currentUserWarehouseId;

    if (currentUserWarehouseId) {
      form.from_warehouse_id = currentUserWarehouseId;
    } else if (settingsRes.status === "fulfilled" && settingsRes.value?.default_warehouse) {
      form.from_warehouse_id = Number(settingsRes.value.default_warehouse);
    } else if (warehouses.value.length > 0) {
      form.from_warehouse_id = Number(warehouses.value[0].id ?? null);
    }

    if (!form.to_warehouse_id) {
      const fallbackDestination = warehouses.value.find(
        (warehouse) => Number(warehouse.id) !== Number(form.from_warehouse_id)
      );
      form.to_warehouse_id = Number(fallbackDestination?.id ?? null);
    }
  };

  const fetchCreateDefaults = async () => {
    try {
      const nextRef = await transferService.getNextReference();
      form.ref = nextRef || buildFallbackTransferRef();
    } catch (error) {
      console.error("Failed to load transfer reference, using fallback.", error);
      form.ref = buildFallbackTransferRef();
    }
  };

  const fetchTransferForEdit = async () => {
    if (!transferId) return;

    const data = await transferService.get(transferId);
    originalTransfer.value = data;
    originalFromWarehouseId.value = Number(data.from_warehouse_id ?? null);

    originalItemsByProductId.value = Object.fromEntries(
      (data.items || []).map((item) => [Number(item.product_id), Number(item.qty || 0)])
    );

    const calculatedTaxRate =
      toNumber(data.total) > 0 && toNumber(data.total_tax) > 0
        ? Math.round((toNumber(data.total_tax) / toNumber(data.total)) * 100)
        : 0;

    form.date = data.created_at ? new Date(data.created_at).toISOString().split("T")[0] : form.date;
    form.from_warehouse_id = Number(data.from_warehouse_id ?? form.from_warehouse_id);
    form.to_warehouse_id = Number(data.to_warehouse_id ?? form.to_warehouse_id);
    form.note = data.note || "";
    form.status = data.status || "completed";
    form.shipping = toNumber(data.shipping);
    form.order_tax = calculatedTaxRate;
    form.discount = 0;
    form.ref = data.ref || form.ref;

    const selectedItems = await Promise.all(
      (data.items || []).map(async (item) => {
        const latestProduct = await productService.getProduct(Number(item.product_id));
        const unitCost = toNumber(item.unit_cost ?? item.cost ?? latestProduct.cost);
        const qty = toNumber(item.qty);
        const availableStock = getEditableStock(Number(item.product_id), latestProduct.stocks, form.from_warehouse_id);

        return {
          ...latestProduct,
          product_id: Number(item.product_id),
          product_name: item.product_name || latestProduct.name,
          product_code: item.product_code || latestProduct.code,
          qty: Math.min(qty, Math.max(1, availableStock)),
          unit_cost: unitCost,
          cost: unitCost,
          tax: toNumber(item.tax),
          subtotal: unitCost * Math.min(qty, Math.max(1, availableStock)),
          available_stock: availableStock,
        } as TransferProduct;
      })
    );

    items.value = selectedItems;
  };

  const fetchData = async () => {
    isLoadingData.value = true;
    try {
      await fetchBaseData();

      if (mode === "create") {
        await fetchCreateDefaults();
      } else {
        await fetchTransferForEdit();
      }

      syncDestinationWarehouse();
    } catch (error) {
      console.error(`Failed to initialize ${mode} transfer page`, error);
      const message =
        mode === "edit"
          ? t("transfer_page.failed_to_load_edit_transfer_data")
          : t("transfer_page.failed_to_load_create_transfer_data");
      Swal.fire({
        icon: "error",
        title: t("transfer_page.error"),
        text: message,
      });

      if (mode === "edit") {
        router.push("/transfer-list");
      }
    } finally {
      isLoadingData.value = false;
    }
  };

  const handleProductSearch = async (query: string) => {
    const normalizedQuery = normalizeSearch(query);

    if (!form.from_warehouse_id) {
      searchResults.value = [];
      showResults.value = false;
      return;
    }

    try {
      const products = await productService.getProducts({
        type: 1,
        warehouse_id: form.from_warehouse_id || undefined,
        search: normalizedQuery || undefined,
      });

      searchResults.value = products
        .map(toTransferProduct)
        .filter((product) => product.available_stock > 0);
      showResults.value = true;
    } catch (error) {
      console.error("Transfer search failed", error);
      searchResults.value = [];
      showResults.value = false;
    }
  };

  const addProduct = (product: Product) => {
    const transferProduct = toTransferProduct(product);
    const existing = items.value.find((item) => item.product_id === transferProduct.product_id);

    if (existing) {
      existing.qty = Math.min(existing.qty + 1, Math.max(1, existing.available_stock));
      existing.subtotal = existing.unit_cost * existing.qty;
    } else {
      items.value.push(transferProduct);
    }

    showResults.value = false;
  };

  const removeItem = (index: number) => {
    items.value.splice(index, 1);
  };

  const updateQty = (index: number, qty: number) => {
    const item = items.value[index];
    if (!item) return;

    const safeQty = Math.max(1, qty);
    item.qty = Math.min(safeQty, Math.max(1, item.available_stock));
    item.subtotal = item.unit_cost * item.qty;
  };

  const updateForm = (key: string, value: number | string) => {
    (form as Record<string, number | string | null>)[key] = value as never;
  };

  const refreshSelectedStocks = async () => {
    if (!form.from_warehouse_id || items.value.length === 0) {
      items.value = items.value.map((item) => ({ ...item, available_stock: 0 }));
      return;
    }

    const refreshedItems = await Promise.all(
      items.value.map(async (item) => {
        try {
          const latestProduct = await productService.getProduct(item.product_id);
          const unitCost = toNumber(latestProduct.cost);
          const availableStock = getEditableStock(item.product_id, latestProduct.stocks, form.from_warehouse_id);
          const nextQty = Math.min(item.qty, Math.max(1, availableStock));

          return {
            ...item,
            ...latestProduct,
            product_id: item.product_id,
            product_name: latestProduct.name,
            product_code: latestProduct.code,
            unit_cost: unitCost,
            cost: unitCost,
            available_stock: availableStock,
            qty: nextQty,
            subtotal: unitCost * nextQty,
          };
        } catch (error) {
          console.error(`Failed to refresh transfer stock for product ${item.product_id}`, error);
          return {
            ...item,
            available_stock: 0,
          };
        }
      })
    );

    items.value = refreshedItems;
  };

  const totalQty = computed(() => items.value.reduce((acc, item) => acc + Number(item.qty), 0));
  const subTotal = computed(() =>
    items.value.reduce((acc, item) => acc + Number(item.unit_cost) * Number(item.qty), 0)
  );
  const grandTotal = computed(() => {
    let total = subTotal.value;
    total += total * (Number(form.order_tax) / 100);
    total += Number(form.shipping);
    total -= Number(form.discount);
    return total;
  });

  const buildPayload = () => {
    const fromWarehouse = warehouses.value.find(
      (warehouse) => Number(warehouse.id) === Number(form.from_warehouse_id)
    );
    const toWarehouse = warehouses.value.find(
      (warehouse) => Number(warehouse.id) === Number(form.to_warehouse_id)
    );

    return {
      ...form,
      from_wh_code: fromWarehouse?.code || "",
      from_wh_name: fromWarehouse?.name || "",
      to_wh_code: toWarehouse?.code || "",
      to_wh_name: toWarehouse?.name || "",
      items: items.value.map((item) => ({
        product_id: item.product_id,
        product_name: item.product_name,
        product_code: item.product_code,
        qty: item.qty,
        unit_cost: item.unit_cost,
        cost: item.cost,
        tax: item.tax,
      })),
      grand_total: grandTotal.value,
      total: subTotal.value,
      total_tax: subTotal.value * (Number(form.order_tax) / 100),
    };
  };

  const validateBeforeSubmit = async () => {
    if (!form.from_warehouse_id || !form.to_warehouse_id) {
      await Swal.fire({
        icon: "error",
        title: t("transfer_page.error"),
        text: t("transfer_page.please_select_both_warehouses"),
      });
      return false;
    }

    if (Number(form.from_warehouse_id) === Number(form.to_warehouse_id)) {
      await Swal.fire({
        icon: "error",
        title: t("transfer_page.error"),
        text: t("transfer_page.source_destination_must_be_different"),
      });
      return false;
    }

    if (items.value.length === 0) {
      await Swal.fire({
        icon: "error",
        title: t("transfer_page.error"),
        text: t("transfer_page.please_add_at_least_one_product"),
      });
      return false;
    }

    const invalidQty = items.value.find((item) => item.qty > item.available_stock);
    if (invalidQty) {
      await Swal.fire({
        icon: "error",
        title: t("transfer_page.error"),
        text: t("transfer_page.product_exceeds_available_stock", { name: invalidQty.product_name }),
      });
      return false;
    }

    return true;
  };

  const submitTransfer = async () => {
    const isValid = await validateBeforeSubmit();
    if (!isValid) return;

    try {
      Swal.fire({
        title: mode === "edit" ? t("transfer_page.updating_transfer") : t("transfer_page.saving_transfer"),
        text:
          mode === "edit"
            ? t("transfer_page.wait_while_update_transfer")
            : t("transfer_page.wait_while_create_transfer"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      const payload = buildPayload();

      if (mode === "edit" && transferId) {
        await transferService.update(transferId, payload);
      } else {
        await transferService.create(payload);
      }

      await Swal.fire({
        icon: "success",
        title: mode === "edit" ? t("transfer_page.transfer_updated") : t("transfer_page.transfer_created"),
        text:
          mode === "edit"
            ? t("transfer_page.stock_transfer_updated_successfully")
            : t("transfer_page.stock_transfer_saved_successfully"),
        timer: 1500,
        showConfirmButton: false,
      });

      router.push("/transfer-list");
    } catch (error: any) {
      console.error(`Failed to ${mode} transfer`, error);
      const msg =
        error.response?.data?.message ||
        error.message ||
        (mode === "edit" ? t("transfer_page.failed_to_edit_transfer") : t("transfer_page.failed_to_create_transfer"));
      Swal.fire({
        icon: "error",
        title: t("transfer_page.error"),
        text: msg,
      });
    }
  };

  watch(
    () => form.from_warehouse_id,
    async (newValue, oldValue) => {
      if (newValue === oldValue) return;
      searchResults.value = [];
      showResults.value = false;
      syncDestinationWarehouse();
      await refreshSelectedStocks();
    }
  );

  onMounted(fetchData);

  return {
    breadcrumbItems,
    warehouses,
    searchResults,
    showResults,
    items,
    form,
    isLoadingData,
    fromWarehouseLockedToProfile,
    handleProductSearch,
    addProduct,
    removeItem,
    updateQty,
    updateForm,
    totalQty,
    subTotal,
    grandTotal,
    submitTransfer,
  };
}

export function useTransferList() {
  const { t } = useI18n();
  const selectedTransfer = ref<Transfer | null>(null);
  const searchQuery = ref("");
  const warehouses = ref<Warehouse[]>([]);
  const transfers = ref<Transfer[]>([]);
  const loading = ref(false);
  const filters = reactive({
    from_warehouse_id: 0,
    to_warehouse_id: 0,
  });
  const activeFilters = ref({
    from_warehouse_id: undefined as number | undefined,
    to_warehouse_id: undefined as number | undefined,
  });

  const fetchWarehouses = async () => {
    try {
      warehouses.value = await warehouseService.getWarehouses();
    } catch (error) {
      console.error("Error fetching warehouses:", error);
    }
  };

  const syncFilters = () => {
    activeFilters.value = {
      from_warehouse_id: filters.from_warehouse_id || undefined,
      to_warehouse_id: filters.to_warehouse_id || undefined,
    };
  };

  const applyFilters = () => {
    syncFilters();
    fetchTransfers();
  };

  const resetFilters = () => {
    filters.from_warehouse_id = 0;
    filters.to_warehouse_id = 0;
    applyFilters();
  };

  const handleViewTransfer = (transfer: Transfer) => {
    selectedTransfer.value = transfer;
  };

  const fetchTransfers = async () => {
    loading.value = true;
    try {
      const allTransfers = await transferService.getAll({
        search: searchQuery.value || undefined,
        ...activeFilters.value,
      });
      const searchText = String(searchQuery.value || "").trim().toLowerCase();

      transfers.value = allTransfers.filter((transfer: Transfer) => {
        const fromWarehouseId = Number(activeFilters.value.from_warehouse_id || 0);
        const toWarehouseId = Number(activeFilters.value.to_warehouse_id || 0);

        const matchesSearch =
          !searchText ||
          String(transfer.ref || "").toLowerCase().includes(searchText) ||
          String(transfer.from_warehouse?.name || transfer.from_wh_name || "").toLowerCase().includes(searchText) ||
          String(transfer.to_warehouse?.name || transfer.to_wh_name || "").toLowerCase().includes(searchText);

        const matchesFromWarehouse =
          !fromWarehouseId || Number(transfer.from_warehouse_id) === fromWarehouseId;
        const matchesToWarehouse =
          !toWarehouseId || Number(transfer.to_warehouse_id) === toWarehouseId;

        return matchesSearch && matchesFromWarehouse && matchesToWarehouse;
      });
    } catch (error) {
      console.error("Failed to fetch transfers", error);
      transfers.value = [];
      Swal.fire({
        icon: "error",
        title: t("transfer_page.failed_to_load"),
        text: t("transfer_page.unable_to_load_stock_transfers"),
      });
    } finally {
      loading.value = false;
    }
  };

  const deleteTransfer = async (transfer: Transfer) => {
    if (!transfer.id) return;

    const result = await Swal.fire({
      title: t("transfer_page.delete_transfer_title"),
      text: t("transfer_page.delete_transfer_text", { reference: transfer.ref }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("transfer_page.confirm_delete"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("transfer_page.deleting"),
        text: t("transfer_page.please_wait"),
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
      });

      await transferService.delete(transfer.id);
      await fetchTransfers();

      Swal.fire({
        icon: "success",
        title: t("transfer_page.deleted"),
        text: t("transfer_page.transfer_deleted_successfully"),
        timer: 1600,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to delete transfer", error);
      Swal.fire({
        icon: "error",
        title: t("transfer_page.delete_failed"),
        text: t("transfer_page.unable_to_delete_transfer"),
      });
    }
  };

  watch(searchQuery, fetchTransfers);

  onMounted(async () => {
    syncFilters();
    await fetchWarehouses();
    await fetchTransfers();
  });

  return {
    selectedTransfer,
    searchQuery,
    warehouses,
    transfers,
    loading,
    filters,
    activeFilters,
    applyFilters,
    resetFilters,
    handleViewTransfer,
    deleteTransfer,
  };
}

export function useTransferForm() {
  return buildTransferComposable({ mode: "create" });
}

export function useTransferEditForm(transferId: number) {
  return buildTransferComposable({ mode: "edit", transferId });
}
